import { supabase } from '../api/supabase'

// Z73.6.1: one status enum + badge scheme shared everywhere a POA's validity is shown --
// eCustoms Preview, Job Detail, POA Management, Customer Detail. 'missing' is a caller-side
// concept (no matching row found) -- usePOAs.ts's own POAStatus type has no such state since
// every row it deals with obviously exists.
export type PoaBadge = 'active' | 'expiring_soon' | 'expired' | 'missing'

export const POA_BADGE_META: Record<PoaBadge, { emoji: string; label: string }> = {
  active: { emoji: '🟢', label: 'ACTIVE' },
  expiring_soon: { emoji: '🟡', label: 'EXPIRING SOON' },
  expired: { emoji: '🔴', label: 'EXPIRED' },
  missing: { emoji: '⚫', label: 'MISSING' },
}

export const POA_BADGE_COLOR_CLASSES: Record<PoaBadge, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  expiring_soon: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  expired: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  missing: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
}

export interface PoaStatus {
  badge: PoaBadge
  poaId: string | null
  validUntil: string | null
  isOpenEnded: boolean
  daysRemaining: number | null
  importerNameOnPoa: string | null
  driveWebLink: string | null
}

// Z73.6.1: exactly mirrors usePOAs.ts's calculateStatus() 30-day threshold (kept as a standalone
// pure function since Job Detail/Customer Detail don't otherwise use the usePOAs hook, and the
// submit-ecustoms edge function -- a separate Deno runtime -- needs its own copy of this same
// rule rather than importing this file).
export function computePoaBadge(poa: { valid_until: string | null; is_open_ended: boolean } | null): { badge: PoaBadge; daysRemaining: number | null } {
  if (!poa) return { badge: 'missing', daysRemaining: null }
  if (poa.is_open_ended || !poa.valid_until) return { badge: 'active', daysRemaining: null }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const expiry = new Date(poa.valid_until)
  expiry.setHours(0, 0, 0, 0)
  const daysRemaining = Math.round((expiry.getTime() - today.getTime()) / 86400000)
  if (daysRemaining < 0) return { badge: 'expired', daysRemaining }
  if (daysRemaining < 30) return { badge: 'expiring_soon', daysRemaining }
  return { badge: 'active', daysRemaining }
}

export function formatPoaStatusLine(status: PoaStatus, eori?: string | null): string {
  const meta = POA_BADGE_META[status.badge]
  const prefix = `POA Status: ${meta.emoji} ${meta.label}`
  if (status.badge === 'missing') return `${prefix} -- no POA on file${eori ? ` for EORI ${eori}` : ''}`
  if (status.badge === 'expired') return `${prefix} on ${status.validUntil} -- cannot proceed, upload renewal`
  if (status.isOpenEnded || !status.validUntil) return `${prefix} (open-ended)`
  if (status.badge === 'expiring_soon') return `${prefix} (expires ${status.validUntil}, ${status.daysRemaining} days) -- renew before submission`
  return `${prefix} (expires ${status.validUntil}, ${status.daysRemaining} days)`
}

// Z73.6 revised: keyed by EORI (not customer_id) -- an importer's POA is issued against their
// EORI, and staff need this to work even before a job/customer link is fully set up. When more
// than one non-deleted POA matches, prefers an open-ended one (unambiguously the most durable),
// else the one expiring latest -- same "ORDER BY expiry_date DESC LIMIT 1" intent Gary specced.
export async function fetchPoaStatusForEori(eori: string | null | undefined): Promise<PoaStatus> {
  const blank: PoaStatus = { badge: 'missing', poaId: null, validUntil: null, isOpenEnded: false, daysRemaining: null, importerNameOnPoa: null, driveWebLink: null }
  if (!eori) return blank

  const { data } = await supabase
    .from('poas')
    .select('id, importer_name, valid_until, is_open_ended, drive_web_link, is_deleted')
    .eq('eori_number', eori)
    .or('is_deleted.is.false,is_deleted.is.null')
    .order('is_open_ended', { ascending: false })
    .order('valid_until', { ascending: false })
    .limit(1)

  const poa = data?.[0] || null
  const { badge, daysRemaining } = computePoaBadge(poa)
  return {
    badge, daysRemaining,
    poaId: poa?.id || null,
    validUntil: poa?.valid_until || null,
    isOpenEnded: poa?.is_open_ended || false,
    importerNameOnPoa: poa?.importer_name || null,
    driveWebLink: poa?.drive_web_link || null,
  }
}
